"use client"

import { Badge, Heading, Input, Text } from "@medusajs/ui"
import React from "react"

import { applyPromotions } from "@lib/data/cart"
import { convertToLocale } from "@lib/util/money"
import { HttpTypes } from "@medusajs/types"
import Trash from "@modules/common/icons/trash"
import { SubmitButton } from "../submit-button"

type DiscountCodeProps = {
  cart: HttpTypes.StoreCart & {
    promotions: HttpTypes.StorePromotion[]
  }
}

const DiscountCode: React.FC<DiscountCodeProps> = ({ cart }) => {
  const [errorMessage, setErrorMessage] = React.useState("")

  const { promotions = [] } = cart

  const removePromotionCode = async (code: string) => {
    const validPromotions = promotions.filter(
      (promotion) => promotion.code !== code
    )

    await applyPromotions(
      validPromotions
        .filter((p) => p.code !== undefined)
        .map((p) => p.code!)
    )
  }

  const addPromotionCode = async (formData: FormData) => {
    setErrorMessage("")

    const code = formData.get("code")

    if (!code) return

    const input = document.getElementById(
      "promotion-input"
    ) as HTMLInputElement

    const codes = promotions
      .filter((p) => p.code !== undefined)
      .map((p) => p.code!)

    codes.push(code.toString())

    try {
      await applyPromotions(codes)
    } catch (e: any) {
      setErrorMessage(e.message)
    }

    if (input) {
      input.value = ""
    }
  }

  return (
    <div className="w-full">

      <form action={(a) => addPromotionCode(a)} className="w-full">

        {/* input + button */}
        <div className="flex w-full items-center gap-2">

          <Input
            id="promotion-input"
            name="code"
            type="text"
            placeholder="Enter promo code"
            data-testid="discount-input"
            className="
              h-10 flex-1
              rounded-xl
              border border-white/15
              bg-white/10
              px-3
              text-sm
              text-white
              placeholder:text-white/40
              focus:border-white/25
              focus:bg-white/15
            "
          />

          <SubmitButton
            data-testid="discount-apply-button"
            className="
              h-10
              rounded-xl
              border-0
              bg-gradient-to-r
              from-[#f97316]
              to-[#fb923c]
              px-4
              text-sm
              font-semibold
              text-white
              shadow-[0_6px_18px_rgba(249,115,22,0.35)]
              hover:opacity-95
              active:scale-[0.98]
            "
          >
            Apply
          </SubmitButton>

        </div>


        {/* error message */}
        {errorMessage && (
          <div
            className="
              mt-3
              w-full
              rounded-lg
              border border-red-400/20
              bg-red-500/10
              px-3 py-2
              text-sm
              text-red-200
            "
            data-testid="discount-error-message"
          >
            {errorMessage}
          </div>
        )}

      </form>


      {/* applied promotions */}
      {promotions.length > 0 && (

        <div
          className="
            mt-4
            w-full
            rounded-xl
            border border-white/10
            bg-white/[0.05]
            p-3
          "
        >

          <Heading
            className="
              mb-2
              text-sm
              font-semibold
              text-white/85
            "
          >
            Promotion applied:
          </Heading>

          {promotions.map((promotion) => {

            return (

              <div
                key={promotion.id}
                className="
                  mb-2
                  flex
                  items-center
                  justify-between
                  gap-2
                  last:mb-0
                "
                data-testid="discount-row"
              >

                <Text
                  className="
                    flex
                    items-baseline
                    gap-1
                    text-sm
                    text-white/90
                  "
                >

                  <Badge
                    color={promotion.is_automatic ? "green" : "grey"}
                    size="small"
                  >
                    {promotion.code}
                  </Badge>

                  <span className="opacity-70">

                    (
                    {promotion.application_method?.value !== undefined &&
                      promotion.application_method.currency_code !==
                        undefined && (

                        <>
                          {promotion.application_method.type === "percentage"
                            ? `${promotion.application_method.value}%`
                            : convertToLocale({
                                amount:
                                  +promotion.application_method.value,
                                currency_code:
                                  promotion.application_method
                                    .currency_code,
                              })}
                        </>
                      )}

                    )

                  </span>

                </Text>


                {!promotion.is_automatic && (

                  <button
                    onClick={() => {

                      if (!promotion.code) return

                      removePromotionCode(promotion.code)

                    }}

                    className="
                      flex
                      h-8 w-8
                      items-center
                      justify-center
                      rounded-full
                      border border-white/10
                      bg-white/5
                      text-white/80
                      transition
                      hover:bg-white/10
                      hover:text-white
                    "

                    data-testid="remove-discount-button"
                  >

                    <Trash size={14} />

                  </button>

                )}

              </div>

            )

          })}

        </div>

      )}

    </div>
  )
}

export default DiscountCode